import React from 'react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  ICommonDraggableTableColumnDef,
  ICommonDraggableTableProps,
} from '../../types/commonDraggableTable';

function SortableRow<T extends { id: string | number }>({
  row,
  columns,
}: Readonly<{
  row: T;
  columns: ICommonDraggableTableColumnDef<T>[];
}>) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: row.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      {/* Drag Handle Column */}
      <TableCell sx={{ width: 50 }}>
        <IconButton {...attributes} {...listeners} size="small" sx={{ cursor: 'grab' }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>

      {/* Data Columns */}
      {columns.map((col) => {
        const value = row[col.field];

        return (
          <TableCell key={String(col.field)}>
            {col.renderCell ? col.renderCell(value, row) : (value as React.ReactNode)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export function CommonDraggableTable<T extends { id: string | number }>({
  columns,
  data,
  onOrderChange,
}: Readonly<ICommonDraggableTableProps<T>>) {
  const [rows, setRows] = React.useState<T[]>(data);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setRows(data);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // prevents accidental drags when clicking buttons
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setRows((prevRows) => {
      const oldIndex = prevRows.findIndex((r) => r.id === active.id);
      const newIndex = prevRows.findIndex((r) => r.id === over.id);

      const updatedRows = arrayMove(prevRows, oldIndex, newIndex);

      if (onOrderChange) {
        void (async () => {
          setIsSaving(true);
          try {
            await onOrderChange(updatedRows);
          } catch {
            console.error('Failed to update order, rolling back');
            setRows(prevRows); // rollback
          } finally {
            setIsSaving(false);
          }
        })();
      }

      return updatedRows;
    });
  };

  return (
    <Box position="relative">
      {isSaving && (
        <Box position="absolute" top={10} right={10} zIndex={10}>
          <CircularProgress size={20} />
        </Box>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {/* Drag Handle Header */}
                <TableCell sx={{ width: 50 }} />

                {columns.map((col) => (
                  <TableCell key={String(col.field)}>{col.headerName}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <SortableContext
              items={rows.map((r) => r.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {rows.map((row) => (
                  <SortableRow key={row.id.toString()} row={row} columns={columns} />
                ))}
              </TableBody>
            </SortableContext>
          </Table>
        </TableContainer>
      </DndContext>
    </Box>
  );
}
